import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { Loader2, RefreshCw, Sparkles } from "lucide-react";
import { instructorApi } from "../../../services/instructorApi";
import OnboardingStepFrame from "../../../components/instructor/OnboardingStepFrame";
import PhotoGallery from "../../../components/PhotoGallery";

export default function ReviewStep() {
  const { instructor, refreshInstructor, user } = useAuth();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  const [bio, setBio] = useState("");
  const [bioLoading, setBioLoading] = useState(false);
  const [bioError, setBioError] = useState("");
  const [bioReady, setBioReady] = useState(false);
  const bioInFlight = useRef(false);
  const bioInitialized = useRef(false);

  const profile = instructor?.profile;
  const completeness = instructor?.completeness;
  const preview = instructor?.preview;

  async function generateBio() {
    if (bioInFlight.current) return;
    bioInFlight.current = true;
    setBioLoading(true);
    setBioError("");
    try {
      const result = await instructorApi.generateBio();
      const next = (result?.bio || "").trim();
      if (!next) {
        throw new Error("Bio generation returned empty text");
      }
      setBio(next);
      return next;
    } catch (err) {
      setBioError(
        err.message || "Could not generate bio. You can retry or write your own."
      );
      return null;
    } finally {
      setBioLoading(false);
      bioInFlight.current = false;
      setBioReady(true);
    }
  }

  React.useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const latest = await refreshInstructor();
        if (cancelled) return;

        const existing = (latest?.profile?.bio || "").trim();
        if (existing) {
          setBio(existing);
          setBioReady(true);
          bioInitialized.current = true;
          return;
        }

        if (bioInitialized.current) return;
        bioInitialized.current = true;
        await generateBio();
      } catch {
        if (!cancelled) {
          setBioReady(true);
          bioInitialized.current = true;
        }
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit() {
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      const latest = await refreshInstructor();
      if (latest?.completeness && !latest.completeness.canSubmit) {
        const items = (latest.completeness.requiredMissing || [])
          .map((m) => m.label)
          .join("; ");
        setError(
          items
            ? `Profile incomplete (${latest.completeness.score}%): ${items}`
            : `Profile incomplete (${latest.completeness.score}%). Finish required steps first.`
        );
        return;
      }
      const wasRejected = profile?.status === "Rejected";
      const trimmedBio = bio.trim();
      if (trimmedBio) {
        await instructorApi.patchProfile({ bio: trimmedBio });
      }
      await instructorApi.submit();
      await refreshInstructor();
      setSuccess(
        wasRejected
          ? "Application resubmitted. We typically review within 2 business days."
          : "Application submitted. We typically review within 2 business days."
      );
    } catch (err) {
      let message = err.message || "Submit failed";
      if (err.data?.completeness?.requiredMissing?.length) {
        const items = err.data.completeness.requiredMissing.map((m) => m.label).join("; ");
        message = `${message} — ${items}`;
      } else if (err.data?.completeness) {
        message = `${message}. Score: ${err.data.completeness.score}%`;
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitChanges() {
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      const trimmedBio = bio.trim();
      if (trimmedBio) {
        await instructorApi.patchProfile({ bio: trimmedBio });
      }
      await instructorApi.submitChanges();
      await refreshInstructor();
      setSuccess(
        "Profile updates submitted for review. Your live profile stays visible until changes are approved."
      );
    } catch (err) {
      setError(err.message || "Could not submit profile changes");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleWithdraw() {
    setError("");
    setSuccess("");
    setWithdrawing(true);
    try {
      await instructorApi.withdraw();
      await refreshInstructor();
      setSuccess("Application withdrawn to Draft. Edit any step, then resubmit for review.");
    } catch (err) {
      setError(err.message || "Could not withdraw application");
    } finally {
      setWithdrawing(false);
    }
  }

  if (!profile) return <div className="text-gray-500">Loading…</div>;

  const canSubmitApplication = ["Draft", "Rejected"].includes(profile.status);
  const canSubmitChanges =
    profile.status === "Approved" &&
    profile.has_pending_review &&
    profile.pending_changes &&
    Object.keys(profile.pending_changes).length > 0;
  const canWithdraw = ["Submitted", "UnderReview"].includes(profile.status);
  const isAwaitingReview = ["Submitted", "UnderReview"].includes(profile.status);
  const canEditBio = canSubmitApplication || profile.status === "Approved";
  const displayBio = bio.trim() || preview?.bio || "";

  return (
    <OnboardingStepFrame
      title="Preview & submit"
      subtitle={`This is how students will see you. Completeness: ${completeness?.score ?? 0}% (need ${completeness?.threshold ?? 85}%).`}
      wide
    >
      <div className="space-y-6">
      {error && <div className="rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
      {success && <div className="rounded-lg bg-emerald-50 text-emerald-800 text-sm px-3 py-2">{success}</div>}

      {!user?.email_verified && (
        <div className="rounded-lg bg-amber-50 text-amber-900 text-sm px-3 py-2">
          Please verify your email before submitting (skipped automatically in local auth mode).
        </div>
      )}

      {profile.status === "Approved" && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-900 text-sm px-3 py-2">
          Your profile is live. You can update any step from the left menu. Everyday fields save
          immediately; sensitive changes (public name, photo) need admin approval — use{" "}
          <strong>Submit updates for review</strong> below when ready.
        </div>
      )}

      {profile.status === "Rejected" && (
        <div className="rounded-lg bg-amber-50 border border-amber-100 text-amber-900 text-sm px-3 py-2">
          {profile.rejection_reason ? (
            <>
              <span className="font-medium">Updates needed:</span> {profile.rejection_reason}{" "}
            </>
          ) : (
            "Updates are needed before we can approve your profile. "
          )}
          Fix the steps on the left, then resubmit for review.
        </div>
      )}

      {isAwaitingReview && (
        <div className="rounded-lg bg-amber-50 border border-amber-100 text-amber-900 text-sm px-3 py-2">
          Your application is {profile.status === "UnderReview" ? "under review" : "submitted"}.
          To make changes, withdraw it first, edit, then resubmit.
        </div>
      )}

      <div className="bg-white border border-gray-100 rounded-2xl p-6">
        <div className="flex gap-4 items-start">
          <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 border">
            {preview?.profile_image_url ? (
              <img src={preview.profile_image_url} alt="" className="w-full h-full object-cover" />
            ) : null}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{preview?.display_name || "Your name"}</h2>
            <p className="text-gray-600">{preview?.headline || "Your headline"}</p>
            <p className="text-sm text-gray-500 mt-1">
              {[preview?.city, preview?.country].filter(Boolean).join(", ")}
            </p>
          </div>
        </div>
        <p className="mt-4 text-sm text-gray-600">
          {preview?.years_teaching != null
            ? `${preview.years_teaching} year${preview.years_teaching === 1 ? "" : "s"} teaching`
            : null}
        </p>
        {displayBio ? (
          <p className="mt-4 text-gray-700 whitespace-pre-wrap text-sm">{displayBio}</p>
        ) : null}
        <div className="mt-4 flex flex-wrap gap-2">
          {(preview?.expertise || []).map((tag) => (
            <span key={tag} className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-800">
              {tag}
            </span>
          ))}
        </div>
        {preview?.gallery_images?.length > 0 && (
          <div className="mt-5">
            <p className="font-medium text-gray-900 mb-2 text-sm">Gallery</p>
            <PhotoGallery images={preview.gallery_images} />
          </div>
        )}
        <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-gray-900 mb-2">Credentials</p>
            <ul className="space-y-1 text-gray-600">
              {(preview?.credentials || []).map((c, i) => (
                <li key={i}>{c.name}{c.issuer ? ` — ${c.issuer}` : ""}</li>
              ))}
              {!preview?.credentials?.length && <li>None yet</li>}
            </ul>
          </div>
          <div>
            <p className="font-medium text-gray-900 mb-2">Offerings</p>
            <ul className="space-y-1 text-gray-600">
              {(preview?.offerings || []).map((o, i) => (
                <li key={i}>
                  {o.title} · {o.currency} {o.price}
                </li>
              ))}
              {!preview?.offerings?.length && <li>None yet</li>}
            </ul>
          </div>
        </div>
      </div>

      {canEditBio && (
        <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Instructor bio</h2>
            </div>
            <button
              type="button"
              disabled={bioLoading}
              onClick={() => generateBio()}
              aria-label={bio ? "Regenerate bio" : "Generate bio"}
              title={bio ? "Regenerate bio" : "Generate bio"}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-500 text-white px-3.5 py-2 text-sm font-semibold hover:from-emerald-700 hover:to-teal-600 disabled:opacity-60 shadow-sm"
            >
              {bioLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : bio ? (
                <RefreshCw size={16} />
              ) : (
                <Sparkles size={16} />
              )}
              <span>{bioLoading ? "Generating…" : bio ? "Regenerate" : "Generate"}</span>
            </button>
          </div>

          {bioLoading && !bioReady && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm px-3 py-3">
              Writing your bio from your profile details…
            </div>
          )}

          {bioError && (
            <div className="rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2 flex flex-wrap items-center justify-between gap-2">
              <span>{bioError}</span>
              <button
                type="button"
                disabled={bioLoading}
                onClick={() => generateBio()}
                className="rounded-md bg-red-600 text-white px-2.5 py-1 text-xs font-medium hover:bg-red-700 disabled:opacity-60"
              >
                Retry
              </button>
            </div>
          )}

          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            disabled={bioLoading && !bio}
            rows={8}
            placeholder={
              bioLoading
                ? "Generating your bio…"
                : "Your bio will appear here. You can also write one yourself."
            }
            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
          />
          <p className="text-xs text-gray-500">
            {bio.trim().length} characters
            {bio.trim() && bio.trim().length < 300
              ? " — save requires at least 300 characters if a bio is provided"
              : ""}
          </p>
        </div>
      )}

      {completeness?.requiredMissing?.length > 0 && canSubmitApplication && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
          <p className="font-medium text-amber-900 mb-2">Finish these before submit</p>
          <ul className="space-y-1 text-sm text-amber-900">
            {completeness.requiredMissing.map((m) => (
              <li key={m.key}>
                <Link to={`/instructor/onboarding/${m.step}`} className="underline">
                  {m.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {canSubmitChanges && (
        <div className="bg-white border border-amber-100 rounded-xl p-4 text-sm text-gray-700">
          <p className="font-medium text-gray-900 mb-1">Pending changes awaiting review</p>
          <ul className="list-disc list-inside text-gray-600">
            {Object.keys(profile.pending_changes).map((key) => (
              <li key={key}>{key.replace(/_/g, " ")}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap justify-between items-center gap-3">
        <Link to="/instructor/onboarding/offerings" className="text-sm text-gray-600 hover:underline">
          Back
        </Link>
        <div className="flex flex-wrap gap-2">
          {canWithdraw && (
            <button
              type="button"
              disabled={withdrawing}
              onClick={handleWithdraw}
              className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"
            >
              {withdrawing ? "Withdrawing…" : "Withdraw to edit"}
            </button>
          )}
          {canSubmitChanges && (
            <button
              type="button"
              disabled={submitting}
              onClick={handleSubmitChanges}
              className="rounded-lg bg-emerald-600 text-white px-5 py-2.5 text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit updates for review"}
            </button>
          )}
          {canSubmitApplication && (
            <button
              type="button"
              disabled={submitting}
              onClick={handleSubmit}
              className="rounded-lg bg-emerald-600 text-white px-5 py-2.5 text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
            >
              {submitting
                ? "Submitting…"
                : profile.status === "Rejected"
                  ? "Resubmit for review"
                  : "Submit for review"}
            </button>
          )}
          {isAwaitingReview && !canSubmitApplication && (
            <button
              type="button"
              disabled
              className="rounded-lg bg-emerald-600 text-white px-5 py-2.5 text-sm font-medium disabled:opacity-60"
            >
              Already submitted
            </button>
          )}
          {profile.status === "Approved" && !canSubmitChanges && (
            <Link
              to="/instructor/onboarding/basics"
              className="rounded-lg border border-emerald-200 text-emerald-800 px-5 py-2.5 text-sm font-medium hover:bg-emerald-50"
            >
              Edit profile
            </Link>
          )}
        </div>
      </div>
      </div>
    </OnboardingStepFrame>
  );
}
